import type { CarrierCargoTableData } from "@/pages/carrier-cargo-list/ui/CargoTable";
import { PACKING_STYLES_LIST } from "@/shared/constants/offer-cargo";
import { formatMoney } from "@/shared/lib/currency";
import { BaseDivider } from "@/shared/ui/base-divider";
import { OfferCargoStatusBadge } from "@/widgets/offer-cargo-status-badge";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link } from "@tanstack/react-router";
import { Badge } from "./badge";
import { BorderedGrid, BorderedGridItem } from "./bordered-grid";

type Props = {
  cargo: CarrierCargoTableData;
};

export const MobileCargoListItem: React.FC<Props> = ({ cargo }) => {
  const packingStyleText = PACKING_STYLES_LIST.find(
    (style) => style.value === cargo.packingStyle,
  );

  return (
    <Stack direction="column" alignItems="stretch" gap={1}>
      {/* ラベル */}
      <Stack direction="row" alignItems="center">
        {cargo.remarks.map((remark) => (
          <OfferCargoStatusBadge
            key={remark}
            statusType="offer_cargo_process_proposal"
            status={remark}
          />
        ))}
      </Stack>

      <Stack direction="column" gap={1.5}>
        {/* 積日時～卸日時 */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            gap: 0.5,
          }}
        >
          <Stack direction="column" gap={0.5} alignItems="flex-start">
            <Badge label="積日時 / 積地" />
            <Typography fontSize="14px" fontWeight={700} color="grey.900">
              {cargo.loadingOn}
            </Typography>
            <Typography fontSize="12px" color="grey.500">
              {cargo.departure}
            </Typography>
          </Stack>

          <ArrowForwardIcon
            width={20}
            height={20}
            sx={{
              marginTop: "26px",
            }}
          />

          <Stack direction="column" gap={0.5} alignItems="flex-start">
            <Badge label="卸日時 / 卸地" />
            <Typography fontSize="14px" fontWeight={700} color="grey.900">
              {cargo.unloadingOn}
            </Typography>
            <Typography fontSize="12px" color="grey.500">
              {cargo.destination}
            </Typography>
          </Stack>
        </Box>

        <BaseDivider />

        {/* 経由地～ボディタイプ */}
        <Stack direction="column" gap={1.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Badge label="経由地" />

            {cargo.transitCount > 0 ? (
              <Typography fontSize="14px" fontWeight={700} color="grey.900">
                {cargo.transitCount}
              </Typography>
            ) : (
              <Typography fontSize="14px" fontWeight={700} color="grey.300">
                ー
              </Typography>
            )}
          </Stack>

          {/* TODO: PCでボディタイプ表示するようになったらこちらも対応する */}
        </Stack>

        {/* 品名/荷姿 */}
        <Stack direction="row" alignItems="center" gap={1}>
          <Badge label="品名/荷姿" />
          <Typography fontSize="12px" color="grey.900">
            {cargo.name}
            {cargo.name && packingStyleText?.label && "/"}
            {packingStyleText?.label}
          </Typography>
        </Stack>

        {/* 受付期限・運賃 */}
        <BorderedGrid>
          <BorderedGridItem size={4}>受付状況</BorderedGridItem>
          <BorderedGridItem size={4}>受付期限</BorderedGridItem>
          <BorderedGridItem size={4}>運賃</BorderedGridItem>

          <BorderedGridItem size={4}>
            {cargo.receptionStatus.data ? (
              <OfferCargoStatusBadge
                statusType="offer_cargo_process_reception"
                status={cargo.receptionStatus.data}
              />
            ) : (
              <Typography fontSize="12px" color="grey.900">
                ー
              </Typography>
            )}
          </BorderedGridItem>
          <BorderedGridItem size={4}>
            {cargo.expiration.replace(/^\d\d\d\d\//, "")}
          </BorderedGridItem>
          <BorderedGridItem size={4}>
            {cargo.shippingFee === 0
              ? "提案ください"
              : formatMoney(cargo.shippingFee)}
          </BorderedGridItem>
        </BorderedGrid>

        <Button
          component={Link}
          to={`/carrier/cargos/${cargo.id}`}
          sx={{
            alignSelf: "stretch",
            position: "relative",
            py: "10px",
            borderRadius: "24px",
            backgroundColor: "grey.100",
            fontSize: "14px",
            fontWeight: 700,
            color: "grey.900",
          }}
        >
          荷物詳細を確認
          <ChevronRightIcon
            width={20}
            height={20}
            sx={{ position: "absolute", right: "10px", top: "10px" }}
          />
        </Button>
      </Stack>
    </Stack>
  );
};
